import type { ElvaChatThemeInput } from './types';
import { hexToHex8, hexToHslComponents, hexToRgba } from './colorUtils';

/**
 * Maps @elva/ui design tokens onto CSS variables consumed by the federated chat bundle
 * (chat.elva.ai). The remote ships its own stylesheet but reads host :root variables.
 */
export function syncElvaChatTheme(theme: ElvaChatThemeInput): void {
  if (typeof document === 'undefined') return;

  const root = document.documentElement;
  const ink = theme.ink;
  const accent = theme.accent;

  const primary = accent[0];
  const primaryHsl = hexToHslComponents(primary);
  const accentWash = hexToRgba(accent[0], theme.dark ? 0.18 : 0.12);
  const accentSoft = hexToRgba(accent[0], theme.dark ? 0.28 : 0.1);
  const borderMed =
    theme.line.startsWith('rgba') || theme.line.length === 9
      ? theme.line
      : hexToHex8(theme.ink[0], theme.dark ? 0.15 : 0.09);

  const iriLinear = `linear-gradient(120deg, ${accent[0]} 0%, ${accent[1]} 28%, ${accent[2]} 55%, ${theme.gold} 80%, ${primary} 100%)`;
  const iriDot = `linear-gradient(135deg, ${accent[0]} 0%, ${accent[1]} 55%, ${theme.gold} 100%)`;

  const shadowSm = theme.dark
    ? '0 1px 2px rgba(0,0,0,0.35), 0 1px 3px rgba(0,0,0,0.25)'
    : '0 1px 2px rgba(28,24,18,0.05), 0 1px 3px rgba(28,24,18,0.04)';
  const shadowMd = theme.dark
    ? '0 4px 12px rgba(0,0,0,0.45), 0 8px 24px rgba(0,0,0,0.35)'
    : '0 1px 3px rgba(28,24,18,0.05), 0 4px 12px rgba(28,24,18,0.06), 0 8px 24px rgba(28,24,18,0.05)';

  root.dataset.elvaChatTheme = theme.dark ? 'dark' : 'light';

  const vars: Record<string, string> = {
    '--background': theme.page,
    '--foreground': ink[0],
    '--card': theme.surface,
    '--card-foreground': ink[0],
    '--popover': theme.surface,
    '--popover-foreground': ink[0],
    '--primary': primaryHsl,
    '--primary-secondary': hexToHslComponents(theme.gold),
    '--primary-foreground': theme.dark ? ink[0] : '0 0% 100%',
    '--secondary': theme.surfaceMute,
    '--secondary-foreground': ink[1],
    '--muted': theme.surfaceMute,
    '--muted-foreground': ink[2],
    '--accent': hexToHslComponents(accent[0]),
    '--accent-foreground': primaryHsl,
    '--destructive': theme.terracotta,
    '--destructive-foreground': theme.dark ? ink[0] : '#ffffff',
    '--border': borderMed,
    '--input': hexToHex8(ink[0], theme.dark ? 0.2 : 0.15),
    '--ring': hexToHex8(primary, 0.28),
    '--sidebar': theme.surfaceMute,
    '--sidebar-foreground': ink[0],
    '--sidebar-primary': primaryHsl,
    '--sidebar-primary-foreground': theme.dark ? ink[0] : '0 0% 100%',
    '--sidebar-accent': theme.surfaceMute,
    '--sidebar-accent-foreground': ink[1],
    '--sidebar-border': borderMed,
    '--sidebar-ring': hexToHex8(primary, 0.28),
    '--elva-bg-deep': theme.page,
    '--elva-surface-subtle': theme.surface,
    '--elva-surface-inset': theme.surfaceMute,
    '--elva-border-med': borderMed,
    '--elva-border-strong': theme.shadowLine.startsWith('rgba')
      ? theme.shadowLine
      : hexToHex8(ink[0], theme.dark ? 0.24 : 0.24),
    '--elva-ink-2': ink[1],
    '--elva-ink-3': ink[2],
    '--elva-ink-4': ink[3],
    '--elva-green': primary,
    '--elva-green-deep': accent[0],
    '--elva-green-light': accent[1],
    '--elva-green-soft': accentSoft,
    '--elva-green-wash': accentWash,
    '--elva-brass': theme.gold,
    '--elva-brass-deep': theme.gold,
    '--elva-brass-tint': hexToRgba(theme.gold, theme.dark ? 0.22 : 0.16),
    '--elva-negative': theme.terracotta,
    '--elva-negative-soft': hexToRgba(theme.terracotta, theme.dark ? 0.2 : 0.1),
    '--elva-iri-linear': iriLinear,
    '--elva-iri-dot': iriDot,
    '--elva-shadow-xs': shadowSm,
    '--elva-shadow-sm': shadowSm,
    '--elva-shadow-md': shadowMd,
  };

  for (const [key, value] of Object.entries(vars)) {
    root.style.setProperty(key, value);
  }
}
